import {
  BdcLineNo,
  BdcShipmentItem,
  BdcShipmentStore,
} from "../../types/bdc.type";
import { compareLineNo, lineText } from "./format";

/**
 * ใบคุมตามสาย — the store × SKU pivot of one shipment, the screen version of the customer's
 * hand-made Excel control sheet: rows สาย → จุดส่ง, one column per SKU that has any quantity,
 * a subtotal per สาย, the grand total, then full cases and the remainder per SKU.
 *
 * Built once per (detail, search) and handed to a plain table: WB01 runs ~2,200 stores, so
 * the render must do no work of its own beyond reading these arrays.
 */

export interface ControlColumn {
  barcode: string;
  item_code: string;
  name: string;
  pcs_per_case: number;
}

export interface ControlRow {
  store_code: string;
  store_name: string;
  stop_no: BdcLineNo;
  /** qty per column, aligned with `columns`. */
  cells: number[];
  total: number;
}

export interface ControlLineBlock {
  line_no: BdcLineNo;
  rows: ControlRow[];
  subtotal: number[];
  total: number;
}

export interface ControlSheet {
  columns: ControlColumn[];
  lines: ControlLineBlock[];
  grandTotal: number[];
  grandTotalPieces: number;
  fullCases: number[];
  remainder: number[];
  storeCount: number;
}

/** Only SKUs any store ordered, in 7-11 item-code order like the prototype. */
export const controlColumns = (
  items: BdcShipmentItem[],
  stores: BdcShipmentStore[],
): ControlColumn[] => {
  const used = new Set<string>();
  stores.forEach((store) => {
    Object.entries(store.qty ?? {}).forEach(([barcode, qty]) => {
      if (qty) used.add(barcode);
    });
  });
  return items
    .filter((item) => used.has(item.barcode))
    .map((item) => ({
      barcode: item.barcode,
      item_code: item.item_code,
      name: item.name,
      pcs_per_case: item.pcs_per_case,
    }))
    .sort((a, b) => a.item_code.localeCompare(b.item_code));
};

/** สาย matches exactly (ignoring padding); store code / name match as a substring. */
export const storeMatches = (store: BdcShipmentStore, query: string): boolean => {
  const q = query.trim();
  if (!q) return true;
  if (lineText(store.line_no) === lineText(q)) return true;
  return store.store_code.includes(q) || store.store_name.includes(q);
};

export const buildControlSheet = (
  items: BdcShipmentItem[],
  stores: BdcShipmentStore[],
  query = "",
): ControlSheet => {
  const columns = controlColumns(items, stores);
  const width = columns.length;
  const sorted = stores
    .filter((store) => storeMatches(store, query))
    .sort(
      (a, b) =>
        compareLineNo(a.line_no, b.line_no) || compareLineNo(a.stop_no, b.stop_no),
    );

  const lines: ControlLineBlock[] = [];
  let current: ControlLineBlock | null = null;
  const grandTotal = new Array<number>(width).fill(0);

  sorted.forEach((store) => {
    if (!current || lineText(current.line_no) !== lineText(store.line_no)) {
      current = { line_no: store.line_no, rows: [], subtotal: new Array<number>(width).fill(0), total: 0 };
      lines.push(current);
    }
    const cells = new Array<number>(width);
    let total = 0;
    for (let i = 0; i < width; i += 1) {
      const qty = store.qty?.[columns[i].barcode] ?? 0;
      cells[i] = qty;
      total += qty;
      current.subtotal[i] += qty;
      grandTotal[i] += qty;
    }
    current.total += total;
    current.rows.push({
      store_code: store.store_code,
      store_name: store.store_name,
      stop_no: store.stop_no,
      cells,
      total,
    });
  });

  const fullCases = grandTotal.map((qty, i) => {
    const per = columns[i].pcs_per_case;
    return per > 0 ? Math.floor(qty / per) : 0;
  });
  const remainder = grandTotal.map((qty, i) => {
    const per = columns[i].pcs_per_case;
    return per > 0 ? qty % per : qty;
  });

  return {
    columns,
    lines,
    grandTotal,
    grandTotalPieces: grandTotal.reduce((sum, qty) => sum + qty, 0),
    fullCases,
    remainder,
    storeCount: sorted.length,
  };
};

/** ✓ on the bulk tab: the PO quantity equals the Σ of the per-store PICKLST lines. */
export const picklistMatches = (item: BdcShipmentItem): boolean =>
  item.picklist_qty !== null && item.picklist_qty !== undefined && item.picklist_qty === item.qty;
